'use client'

import { useMemo } from 'react'
import { IconDiscount2 } from '@tabler/icons-react'
import clsx from 'clsx'
import Image from 'next/image'
import Link from 'next/link'

import { LoginModal } from 'modules/auth'
import { DateOfBirthModal } from 'modules/customer'
import { SeoListName } from 'shared/analytics'
import { Collapse } from 'shared/ui/Collapse'
import { Tooltip } from 'shared/ui/Tooltip'
import { capitalizeWords } from 'shared/utils/common'
import { isNotNilOrEmpty } from 'shared/utils/ramda'

import useProductCard from '../../hooks/useProductCard'
import { ProductActionButton, ProductReplaceButton } from '../../ports'
import { ProductTypes } from '../../types/products'
import AddToFavouritesButton from '../AddToFavouritesButton'
import ProductCardBadgesList from '../ProductCard/components/ProductCardBadgesList'
import ProductCardLabelsList from '../ProductCard/components/ProductCardLabelsList'
import ProductCardPrices from '../ProductCard/components/ProductCardPrices'
import ProductCardProductSets from '../ProductCard/components/ProductCardProductSets'

import shared from '../ProductCard/ProductCard.module.scss'
import styles from './MenuPromotionProductCard.module.scss'

const RECOMMENDS_LIST_NAME = SeoListName.RECOMMENDS_LIST_NAME

type ProductCardProps = {
  productPath: string
  product: ProductTypes
  seoButtonPlace: string
  ageLimitMetCategory?: boolean
  realProductId?: string
  isSubstitute?: boolean
  onClickSeo?: () => void
  idxOnList: number
  id?: string
  listName: SeoListName
}

const MenuPromotionProductCard = ({
  productPath,
  ageLimitMetCategory,
  product,
  realProductId,
  isSubstitute,
  onClickSeo,
  seoButtonPlace,
  idxOnList,
  id,
  listName
}: ProductCardProps): React.ReactNode => {
  const {
    loginModalOpen,
    handleCloseLoginModal,
    dobModalOpen,
    handleCloseDobModal,
    blurProduct,
    blurProductMessage,
    handleBlurredProductClick,
    parsedBadges,
    name,
    nameDescriptionShort,
    nameShort,
    productImage,
    handleMouseOut,
    handleMouseOver,
    hover,
    unitDetails,
    pricePerUnit,
    parsedTags,
    quantityJump,
    maxQuantityForSpecialOffer,
    setQuantityJump,
    brandPath,
    path
  } = useProductCard({ product, ageLimitMetCategory, productPath })

  const capitalizedShortName = useMemo(
    () => capitalizeWords(nameShort),
    [nameShort]
  )

  return (
    <div className={shared.globalWrapper} id={id}>
      <LoginModal open={loginModalOpen} onClose={handleCloseLoginModal} />
      <DateOfBirthModal open={dobModalOpen} onClose={handleCloseDobModal} />
      {blurProduct && (
        <button
          className={shared.blurredIconWrapper}
          onClick={handleBlurredProductClick}
        >
          <Image
            src='/static/UnderEighteen.svg'
            alt='poniżej 18 lat'
            width={64}
            height={64}
          />
          <div className={shared.blurredInfoWrapper}>{blurProductMessage}</div>
        </button>
      )}
      <div className={clsx(styles.wrapper, { [styles.blur]: blurProduct })}>
        {!blurProduct && (
          <div className={shared.favIconWrapper}>
            {isNotNilOrEmpty(parsedBadges) && (
              <ProductCardBadgesList
                rootCategorySlug={product.category?.slug}
                badges={parsedBadges}
              />
            )}
            <AddToFavouritesButton
              className={shared.rightAddToFavouritesButton}
              id='product_card_favourites_button'
              product={product}
              idxOnList={idxOnList}
              listName={listName}
              seoButtonPlace={seoButtonPlace}
            />
          </div>
        )}

        <Link className={styles.imageWrapper} href={path} title={name}>
          <Image
            className={styles.styledImage}
            title={name}
            src={productImage}
            alt={name}
            fill
            onClick={onClickSeo}
          />
        </Link>
        <div className={shared.productDescription}>
          <div
            className={shared.collapseWrapper}
            onMouseEnter={handleMouseOver}
            onMouseLeave={handleMouseOut}
          >
            <div className={shared.basicDetails}>
              <Link href={brandPath || ''}>
                <h4 className={shared.madeBy}>{capitalizedShortName}</h4>{' '}
              </Link>
              <Link href={path} title={name}>
                <Tooltip
                  id='product-name-tooltip'
                  title={nameDescriptionShort}
                  disableHoverListener={
                    (nameDescriptionShort?.length || 0) < 50
                  }
                >
                  <h3 className={shared.name}>
                    {nameDescriptionShort || name || capitalizedShortName}
                  </h3>
                </Tooltip>
              </Link>
            </div>
            <div className={shared.grammarWrapper}>
              <p className={shared.grammar}>{unitDetails}</p>
              <p
                className={shared.pricePerGrammar}
                dangerouslySetInnerHTML={{ __html: pricePerUnit }}
              />
            </div>
            {isNotNilOrEmpty(parsedTags) && (
              <div className={shared.tagsWrapper}>
                <ProductCardLabelsList labels={parsedTags} />
              </div>
            )}
            <ProductCardPrices
              isHovered={hover}
              product={product}
              quantityJump={quantityJump}
            />
            {isNotNilOrEmpty(maxQuantityForSpecialOffer) &&
              product.showRemainingSpecialOfferQuantity && (
                <Collapse open={hover}>
                  <div className={shared.maxPerSpecialOfferWrapper}>
                    <IconDiscount2 size={30} />
                    <p>
                      Maksymalna liczba dostępna w promocji:{' '}
                      {maxQuantityForSpecialOffer}
                    </p>
                  </div>
                </Collapse>
              )}
            <ProductCardProductSets
              isHovered={hover}
              quantityJump={quantityJump}
              product={product}
              onQuantityJumpChange={setQuantityJump}
            />
          </div>
          <div
            className={clsx(shared.buttonWrapper, 'product-thumbnail-buttons')}
          >
            {isSubstitute ? ( // if they are isSubstitute, that means we operate on modal of replacements
              <ProductReplaceButton
                realProductId={realProductId} // real product
                substitute={product} // replacement
                idxOnList={idxOnList}
              />
            ) : (
              <ProductActionButton
                variant='primaryOutline'
                product={product}
                quantityJump={quantityJump}
                disabled={blurProduct}
                seoButtonPlace={seoButtonPlace}
                idxOnList={idxOnList}
                listName={RECOMMENDS_LIST_NAME}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default MenuPromotionProductCard
